import React, { useState, useEffect } from 'react';
import { Calendar, ChevronRight, Shield, Clock, MapPin } from 'lucide-react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom'; // Link used for navigation to /players
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { supabase } from '../lib/supabase';
import { Badge } from '@/components/ui/badge';

interface Team {
  id: string;
  name: string;
  description: string;
  sex: string;
  created_at: string;
  coach_type?: string;
}

interface AgeGroup {
  id: string;
  group_name: string;
  sex: 'male' | 'female';
}

interface EvaluationSession {
  id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  location_name: string;
  evaluation_session_age_groups: Array<{
    age_group_id: string;
    age_groups: AgeGroup;
  }>;
}

export function CoachPortal() {
  const { user, profile } = useAuth();
  // Use a type that matches the actual data shape from Supabase
  const [sessions, setSessions] = useState<EvaluationSession[]>([]);
  const [assignedTeams, setAssignedTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forceDate = searchParams.get('force');

  // Fetch today's evaluation sessions
  useEffect(() => {
    async function fetchSessions() {
      setLoading(true);
      setError('');
      
      try {
        // Check if we have a forced session date from query params
        if (forceDate) {
          // Fetch the specific session(s) by date
          const { data, error: sessionError } = await supabase
            .from('evaluation_sessions')
            .select(`
              id,
              session_date,
              start_time,
              end_time,
              location_name,
              evaluation_session_age_groups!inner(
                age_group_id,
                age_groups(id, group_name, sex)
              )
            `)
            .eq('session_date', forceDate)
            .order('start_time', { ascending: true });

          if (sessionError) {
            throw sessionError;
          }

          if (data && data.length > 0) {
            // Transform data to match our TypeScript interface
            const typedData = data.map(session => ({
              ...session,
              evaluation_session_age_groups: session.evaluation_session_age_groups.map((group: any) => ({
                age_group_id: group.age_group_id,
                age_groups: group.age_groups
              }))
            }));
            setSessions(typedData);
          } else {
            setError(`No evaluation session found with date: ${forceDate}`);
          }
        } else {
          // No forced session, so fetch today's sessions
          const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
          const { data, error: sessionError } = await supabase
            .from('evaluation_sessions')
            .select(`
              id,
              session_date,
              start_time,
              end_time,
              location_name,
              evaluation_session_age_groups!inner(
                age_group_id,
                age_groups(id, group_name, sex)
              )
            `)
            .eq('session_date', today)
            .order('start_time', { ascending: true });

          if (sessionError) {
            throw sessionError;
          }

          if (data) {
            // Transform data to match our TypeScript interface
            const typedData = data.map(session => ({
              ...session,
              evaluation_session_age_groups: session.evaluation_session_age_groups.map((group: any) => ({
                age_group_id: group.age_group_id,
                age_groups: group.age_groups
              }))
            }));
            setSessions(typedData);
          } else {
            setSessions([]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    }
    fetchSessions();
  }, [forceDate]);



  // Fetch assigned teams for the current coach
  useEffect(() => {
    async function fetchAssignedTeams() {
      if (!user) return;
      
      try {
        // Fetch teams where the coach is assigned
        const { data, error } = await supabase
          .from('teams')
          .select(`
            id,
            name,
            description,
            sex,
            created_at,
            team_coaches!inner(type)
          `)
          .eq('team_coaches.coach_id', user.id);

        if (error) {
          console.error("Error fetching assigned teams:", error);
          return;
        }

        if (data && data.length > 0) {
          // Transform data to match our Team interface
          const teamsData: Team[] = data.map(team => ({
            id: team.id,
            name: team.name,
            description: team.description,
            sex: team.sex,
            created_at: team.created_at,
            coach_type: team.team_coaches[0]?.type || 'Coach'
          }));
          setAssignedTeams(teamsData);
        } else {
          setAssignedTeams([]);
        }
      } catch (err) {
        console.error('Error in fetchAssignedTeams:', err);
      }
    }
    
    fetchAssignedTeams();
  }, [user]);

  const handleSelectSession = (session: EvaluationSession) => {
    // Navigate to the Evaluation page with the evaluation session ID in the URL
    navigate(`/evaluation/${session.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        {/* Hero Banner */}
        <section className="bg-gradient-to-r from-indigo-600 to-blue-500 text-white p-6 rounded-lg shadow-lg mb-8">
          <h1 className="text-3xl font-bold">
            Welcome back, {profile?.first_name || 'Coach'}!
          </h1>
          <p className="mt-2 text-lg">Here are your upcoming sessions and assignments.</p>
        </section>

        {/* View Registrations Button */}
        <div className="flex justify-end mb-8">
          <Link
            to="/players"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-indigo-600 text-white font-semibold shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 transition-all text-base shadcn-ui-button"
            style={{ textDecoration: 'none' }}
            aria-label="View player registrations"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2h5m6 0v-6m0 6a2 2 0 01-2-2h4a2 2 0 01-2 2zm-6-6V7a4 4 0 014-4h4a4 4 0 014 4v7" /></svg>
            View Registrations
          </Link>
        </div>

        {/* Assigned Teams Section */}
        <section className="bg-white p-6 rounded-lg shadow mb-8">
          <div className="flex items-center mb-4">
            <Shield className="w-6 h-6 text-indigo-600 mr-2" />
            <h2 className="text-xl font-bold">Your Teams</h2>
          </div>
          
          {loading ? (
            <p className="text-gray-500">Loading your teams...</p>
          ) : assignedTeams.length === 0 ? (
            <p className="text-gray-500">You are not currently assigned to any teams.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assignedTeams.map((team) => (
                <Link 
                  to={`/team-roster/${team.id}`}
                  key={team.id} 
                  className="block border rounded-lg p-4 hover:bg-indigo-50 cursor-pointer transition"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{team.name}</h3>
                      <p className="text-sm text-gray-500 mb-2">
                        {team.sex === 'male' ? 'Boys' : 'Girls'} • {team.coach_type || 'Coach'}
                      </p>
                      <p className="text-sm text-gray-700">{team.description || 'No description available'}</p>
                    </div>
                    <div className="flex justify-end">
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
        


        {/* Today's Evaluations Section */}
        <section>
          <div className="flex items-center mb-4">
            <Calendar className="w-8 h-8 text-indigo-600 mr-2" />
            <h2 className="text-2xl font-bold text-gray-800">Today's Evaluations</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-100 text-red-700 p-4 rounded-lg">
              <p>{error}</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="bg-white p-6 rounded-lg shadow text-center">
              <p className="text-gray-600">No evaluations scheduled for today.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => handleSelectSession(session)}
                  className="group cursor-pointer bg-white rounded-lg border border-gray-200 hover:border-indigo-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  {/* Card Header with Start Time */}
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50 group-hover:bg-gray-100 transition-colors">
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-indigo-600 mr-2" />
                      <h3 className="font-semibold text-gray-900">
                        {new Date(`2000-01-01T${session.start_time}`).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </h3>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    {/* Age Groups */}
                    <div className="mb-6">
                      <div className="flex flex-wrap gap-2">
                        {session.evaluation_session_age_groups && session.evaluation_session_age_groups.length > 0 ? (
                          session.evaluation_session_age_groups.map((groupInfo) => (
                            groupInfo.age_groups && (
                              <Badge 
                                key={groupInfo.age_group_id} 
                                variant="outline-sm"
                                className={`text-sm py-1.5 px-3 font-medium border ${groupInfo.age_groups.sex === 'male' ? 
                                  'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' : 
                                  'bg-pink-50 border-pink-200 text-pink-700 hover:bg-pink-100'
                                }`}
                              >
                                {groupInfo.age_groups.group_name} {groupInfo.age_groups.sex === 'male' ? 'Boys' : 'Girls'}
                              </Badge>
                            )
                          ))
                        ) : (
                          <span className="text-gray-500 italic">No age groups assigned</span>
                        )}
                      </div>
                    </div>

                    {/* Session Details */}
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                        <span>{session.location_name}</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        <span>
                          {new Date(session.session_date + 'T00:00:00').toLocaleDateString(undefined, {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-indigo-600 font-medium">
                          {new Date(`2000-01-01T${session.start_time}`).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })} - {new Date(`2000-01-01T${session.end_time}`).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default CoachPortal;